/**
 * Schalter — etwas ist an oder aus, und es wirkt sofort.
 *
 * Übernommen aus `components/forms/Switch.jsx`. Der Unterschied zum
 * Kontrollkästchen ist keine Geschmacksfrage: Ein `Checkbox` sammelt eine
 * Angabe, die erst mit dem Absenden eines Formulars gilt; ein `Switch` schaltet
 * unmittelbar. Wer ihn in ein Formular mit „Speichern" setzt, verspricht etwas,
 * das die Oberfläche nicht hält.
 *
 * Umgesetzt als `<button role="switch">`, nicht als `<div onClick>`: So ist er
 * mit Leer- und Eingabetaste bedienbar und wird als „Schalter, aus" angesagt.
 */
import { useId } from "react";
import { fontWeight } from "../tokens.js";

export interface SwitchProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  /** Was geschaltet wird. Sichtbar, es sei denn, `labelHidden` ist gesetzt. */
  readonly label: string;
  /**
   * Blendet die Beschriftung aus — für Leisten, in denen der Zusammenhang aus
   * dem Umfeld hervorgeht. Die Sprachausgabe bekommt sie trotzdem.
   */
  readonly labelHidden?: boolean;
  readonly disabled?: boolean;
  readonly size?: "sm" | "md";
}

const SIZES = {
  sm: { width: 34, height: 20, knob: 14 },
  md: { width: 42, height: 24, knob: 18 },
} as const;

export function Switch({
  checked,
  onChange,
  label,
  labelHidden = false,
  disabled = false,
  size = "md",
}: SwitchProps) {
  const metrics = SIZES[size];
  const travel = metrics.width - metrics.knob - 6;
  const labelId = useId();

  const control = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      /**
       * Bei sichtbarer Beschriftung wird auf sie verwiesen, statt den Text zu
       * verdoppeln: Ein `<label>` um einen `<button>` verbindet sich nicht mit
       * ihm — anders als beim Kontrollkästchen, wo ein echtes `<input>`
       * darunterliegt.
       */
      aria-label={labelHidden ? label : undefined}
      aria-labelledby={labelHidden ? undefined : labelId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        width: metrics.width,
        height: metrics.height,
        flexShrink: 0,
        padding: 3,
        border: "none",
        borderRadius: "var(--radius-pill)",
        /**
         * Der eingeschaltete Zustand trägt die Markenfarbe. Das ist zulässig —
         * ein Schalter ist Bedienung, keine Datendarstellung (ADR 0013).
         */
        background: checked ? "var(--hm-green-500)" : "var(--border-default)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "background .15s ease",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: metrics.knob,
          height: metrics.knob,
          borderRadius: "50%",
          /* Nicht die Kartenfläche: Im dunklen Thema ist die fast so dunkel wie
             der Zug, und der Knopf verschwände. Er ist „das Helle auf dem Zug". */
          background: "var(--surface-knob)",
          boxShadow: "var(--shadow-xs)",
          transform: `translateX(${checked ? travel : 0}px)`,
          transition: "transform .15s cubic-bezier(.4,0,.2,1)",
        }}
      />
    </button>
  );

  if (labelHidden) return control;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        fontSize: 13.5,
        fontWeight: fontWeight.medium,
        color: "var(--text-body)",
      }}
    >
      {control}
      <span id={labelId}>{label}</span>
    </span>
  );
}
